import React, { useCallback, useEffect, useRef, useState } from "react";
import { ToastContainer } from "react-toastify";
import { FiCheckCircle, FiClock, FiFileText, FiLock, FiShield } from "react-icons/fi";
import { useSelector } from "react-redux";
import parse from "html-react-parser";
import DOMPurify from "dompurify";
import { isDTS } from "../../../utility/platform";
import { findCmsEntryBySlug } from "../../../utility/cmsContent";
import Captcha from "../../common-components/Captcha";
import DashboardLayout from "../../layout/dashboardLayout";
import { createKycSession, getMyKycStatus, refreshKycStatus } from "../../actions/kyc";
import { setAuthorization } from "../../config/axios";
import { setAuthToken } from "../../lib/localStorage";
import { toastAlert } from "../../lib/toastAlert";
import "../../../style/dts-ui.css";

/**
 * Stage 6 — identity verification.
 *
 * Document capture happens entirely on the vendor's own domain: this page's job
 * is to mint a session, hand the user off, and reflect the result when they come
 * back. No identity document ever passes through this app, which is what makes
 * "documents are not stored in a public bucket and not logged" true by
 * construction rather than by policy.
 *
 * Two arrival paths have to work, and only one of them is the happy path:
 *   - the vendor's webhook lands first, so the status is already final on load;
 *   - the user beats the webhook back to the site, so we ask the backend to poll
 *     the vendor once on return (`refreshKycStatus`).
 * Without the second, a user who finishes verification and immediately returns
 * sits on "pending" until they happen to reload later.
 */

// Statuses where the vendor still owes us an answer — worth one poll on return.
const IN_FLIGHT = ["created", "pending"];

// Tones are the DTS dark-theme alert (style/dts-ui.css), not Bootstrap's
// `alert alert-*`. Bootstrap's alerts keep their pale background here but lose
// their dark text to index.css's `* { color: var(--white) !important }`, which
// left this page's status banner at 1.17:1 contrast — white on light cyan.
function statusCopy(status, provider) {
  switch (status) {
    case "approved":
      return {
        tone: "dts-alert dts-alert--success",
        title: "Identity verified",
        body: "You're verified. Redemptions and AMOE code requests are unlocked.",
      };
    case "needs_review":
      return {
        tone: "dts-alert dts-alert--info",
        title: "Verification under review",
        body: "Our team is reviewing your submission. This usually takes one business day — we'll update this page when it's done.",
      };
    case "created":
    case "pending":
      return {
        tone: "dts-alert dts-alert--info",
        title: "Verification in progress",
        body: "We're waiting on the result of your identity check. You can pick up where you left off below.",
      };
    case "declined":
      return {
        tone: "dts-alert dts-alert--warning",
        title: "Verification unsuccessful",
        body: "We couldn't verify your identity from what was submitted. You can try again with a different document.",
      };
    case "expired":
      return {
        tone: "dts-alert dts-alert--neutral",
        title: "Verification expired",
        body: "Your verification session timed out before it was finished. Start a new one below.",
      };
    case "failed":
      return {
        tone: "dts-alert dts-alert--warning",
        title: "Verification could not be completed",
        body: "Something went wrong during your check. You can start a new one below.",
      };
    default:
      return {
        tone: "",
        title: "Verify your identity",
        body: provider === "manual"
          ? "Submit a request for admin review before requesting an AMOE code or redeeming prizes. This page does not collect identity documents."
          : "Sweepstakes rules require an identity check before you can request an AMOE code or redeem prizes. Have a government-issued photo ID ready for the hosted verification flow.",
      };
  }
}

function KycVerification() {
  const cmsData = useSelector((store) => store.cmsData?.cmsData);
  const guide = findCmsEntryBySlug(cmsData, "kyc-how-it-works");
  const guideHtml = DOMPurify.sanitize(guide?.content || "");
  const guideProbe = document.createElement("div");
  guideProbe.innerHTML = guideHtml;
  const hasGuide = Boolean(guideProbe.textContent.trim() || guideProbe.querySelector("img"));
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusUnavailable, setStatusUnavailable] = useState(false);
  const [captchaToken, setCaptchaToken] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const captchaRef = useRef(null);
  // Guards against a second refresh when React re-runs effects (StrictMode) —
  // each refresh is a real vendor API call on the backend's side.
  const refreshedRef = useRef(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getMyKycStatus();
      if (!res?.status || !res.result) {
        setStatusUnavailable(true);
        return null;
      }
      setState(res.result);
      setStatusUnavailable(false);
      return res.result;
    } catch {
      setStatusUnavailable(true);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const current = await load();
      if (cancelled || !current || refreshedRef.current) return;
      // Always reconcile through the uncached POST endpoint. Dynamic KYC GET
      // responses have previously been served stale by the local proxy/browser,
      // leaving an approved user on the CAPTCHA form. The backend returns early
      // for approved/not-started records, so this does not create a Persona API
      // call for those states.
      refreshedRef.current = true;
      const refreshed = await refreshKycStatus();
      if (!cancelled && refreshed.status && refreshed.result) {
        setState((prev) => ({ ...prev, ...refreshed.result }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [load]);

  const handleStart = async () => {
    if (!captchaToken) {
      toastAlert("error", "Please complete the CAPTCHA challenge.", "kyc");
      return;
    }
    setSubmitting(true);
    const res = await createKycSession(captchaToken, `${window.location.origin}/kyc`);
    setSubmitting(false);
    captchaRef.current?.reset();
    setCaptchaToken("");

    if (!res.status) {
      // The start endpoint is authoritative too. If a stale status read left
      // the form visible but the backend confirms approval, render the verified
      // state immediately instead of showing an error and the dead form.
      if (res.code === "already_approved") {
        setState((prev) => ({
          ...prev,
          status: "approved",
          userStatus: "approved",
          canStart: false,
        }));
        toastAlert("success", "Identity verification is complete.", "kyc");
        return;
      }
      toastAlert("error", res.message || "Could not start verification.", "kyc");
      load();
      return;
    }

    // Persist the fresh application session returned by the same successful
    // request before navigating away to Persona.
    if (res.token) {
      setAuthToken(res.token);
      setAuthorization(res.token);
    }

    // Hosted flow: hand the user off to the vendor's domain. They come back to
    // the redirectUri above, where the effect polls for the result.

    const hostedFlowUrl = res.result?.oneTimeLink || res.result?.hostedFlowUrl;
    if (hostedFlowUrl) {
      window.location.href = hostedFlowUrl;
      return;
    }

    // No hosted link (no vendor configured, or a resumed inquiry) — the request
    // is filed for manual review instead.
    toastAlert("success", res.message || "Verification request submitted.", "kyc");
    load();
  };

  if (!isDTS()) return null;
  if (loading) {
    return (
      <DashboardLayout>
        <main className="dts-compliance-page">
          <div className="dts-compliance-shell" aria-busy="true">
            <div className="dts-skeleton dts-skeleton--heading" />
            <div className="dts-skeleton dts-skeleton--card" />
          </div>
        </main>
      </DashboardLayout>
    );
  }

  if (statusUnavailable) {
    return <DashboardLayout><main className="dts-compliance-page"><div className="dts-compliance-shell"><div className="dts-alert dts-alert--warning" role="alert"><strong>Could not check your verification status</strong><p>Please retry before starting another identity check. If this continues, contact support.</p></div><button type="button" className="dts-btn" onClick={load}>Retry status check</button></div></main></DashboardLayout>;
  }

  const status = state?.status || "not_started";
  const copy = statusCopy(status, state?.provider);
  const canStart = state?.canStart !== false;
  const isResumable = IN_FLIGHT.includes(status);

  return (
    <DashboardLayout>
      <main className="dts-compliance-page dts-kyc">
        <ToastContainer />
        <div className="dts-compliance-shell">
        <header className="dts-compliance-header dts-kyc-hero">
          <div className="dts-kyc-hero__copy">
            <span className="dts-eyebrow"><FiShield aria-hidden="true" /> Account security</span>
            <h1 className="dts-page__title">Identity verification</h1>
            <p className="dts-page__lead">Confirm your identity to request mail-in entries and redeem eligible prizes.</p>
            <div className="dts-kyc-hero__facts" aria-label="Verification process">
              <span><FiLock aria-hidden="true" /> Secure process</span>
              <span><FiCheckCircle aria-hidden="true" /> Status shown here</span>
            </div>
          </div>
          <svg className="dts-kyc-hero__art" viewBox="0 0 300 240" aria-hidden="true" focusable="false">
            <defs><linearGradient id="kyc-shield" x1="0" y1="0" x2="1" y2="1"><stop stopColor="#e4f4ff"/><stop offset="1" stopColor="#80b8ed"/></linearGradient></defs>
            <circle cx="150" cy="120" r="104" fill="#002e64" opacity=".25"/>
            <path d="M150 24 242 58v60c0 58-34 99-92 125-58-26-92-67-92-125V58z" fill="url(#kyc-shield)" stroke="#e4f4ff" strokeWidth="3"/>
            <path d="M150 43 225 71v47c0 46-27 79-75 103-48-24-75-57-75-103V71z" fill="#0d57a1"/>
            <circle cx="150" cy="104" r="23" fill="#cee9ff"/>
            <path d="M108 164c5-22 22-35 42-35s37 13 42 35" fill="none" stroke="#cee9ff" strokeWidth="17" strokeLinecap="round"/>
            <circle cx="225" cy="181" r="28" fill="#0bc391" stroke="#d7fff3" strokeWidth="3"/>
            <path d="m213 180 9 9 16-19" fill="none" stroke="#fff" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </header>

        <div className="dts-compliance-grid">
          <section className="dts-panel dts-panel--primary" aria-labelledby="verification-status-title">
            <div className="dts-kyc-status-heading">
              <span className="dts-panel__icon"><FiShield aria-hidden="true" /></span>
              <div><h2>Your verification</h2><p>Track your identity check and complete any next step here.</p></div>
            </div>
            {copy.tone ? (
              <div className={copy.tone} role="status">
                <strong id="verification-status-title" className="dts-alert__title">{copy.title}</strong>
                <p className="dts-alert__body mb-0">{copy.body}</p>
              </div>
            ) : (
              <div className="dts-panel__intro">
                <div className="dts-panel__icon"><FiShield aria-hidden="true" /></div>
                <div>
                  <h2 id="verification-status-title" className="dts-panel__title">{copy.title}</h2>
                  <p className="dts-panel__copy">{copy.body}</p>
                </div>
              </div>
            )}

            {/* Nothing for the user to do while they're verified, or while a human is
                deciding their case — showing a disabled button there reads as an error. */}
            {status === "approved" || status === "needs_review" ? (
              <div className="dts-completion-note">
                {status === "approved" ? <FiCheckCircle aria-hidden="true" /> : <FiClock aria-hidden="true" />}
                <span>{status === "approved" ? "No further action is required." : "You can safely leave this page and return later."}</span>
              </div>
            ) : (
              <div className="dts-action-box">
                <div className="dts-action-box__heading">
                  <FiLock aria-hidden="true" />
                  <div>
                    <strong>Secure verification</strong>
                    <span>Complete the CAPTCHA, then continue to identity verification.</span>
                  </div>
                </div>
                <Captcha ref={captchaRef} onChange={setCaptchaToken} className="dts-captcha" />
                <button
                  type="button"
                  className="dts-btn"
                  disabled={submitting || (!canStart && !isResumable)}
                  onClick={handleStart}
                >
                  {submitting
                    ? "Starting…"
                    : isResumable
                    ? "Continue verification"
                    : status === "not_started"
                    ? "Start verification"
                    : "Try again"}
                </button>
                {!canStart && !isResumable && (
                  <p className="dts-inline-error" role="alert">
                    You've used all available verification attempts. Please contact support to continue.
                  </p>
                )}
              </div>
            )}
          </section>

          <aside className="dts-panel dts-panel--aside" aria-label="Identity verification information">
            <h2 className="dts-panel__title">{hasGuide ? guide.title || "How verification works" : state?.provider === "manual" ? "What happens next" : "What you’ll need"}</h2>
            {hasGuide ? (
              <div className="dts-aside-cms-content">{parse(guideHtml)}</div>
            ) : state?.provider === "manual" ? (
              <ul className="dts-check-list">
                <li><FiFileText aria-hidden="true" /><span><strong>Request a review</strong><small>Start the verification request below.</small></span></li>
                <li><FiClock aria-hidden="true" /><span><strong>Wait for a decision</strong><small>Check this page for your updated status.</small></span></li>
              </ul>
            ) : (
              <ul className="dts-check-list">
                <li><FiFileText aria-hidden="true" /><span><strong>Government-issued photo ID</strong><small>Use a valid, unexpired document.</small></span></li>
                <li><FiCheckCircle aria-hidden="true" /><span><strong>Matching account details</strong><small>Your legal information should match your profile.</small></span></li>
                <li><FiClock aria-hidden="true" /><span><strong>Complete the hosted check</strong><small>Return here to see your status.</small></span></li>
              </ul>
            )}
            <div className="dts-privacy-note"><FiLock aria-hidden="true" /><span>This site does not collect or store ID images. If a hosted check opens, you submit your ID directly to the verification partner.</span></div>
          </aside>
        </div>
        </div>
      </main>
    </DashboardLayout>
  );
}

export default KycVerification;
